import type { PermissionRequest } from '@gestion-permisos/contracts';
import { z } from 'zod';
import { isNetworkFailure } from '../api/intent.js';
import { ApiProblem } from '../api/problem.js';
import { ProblemMessage } from '../components/problem-message.js';
import type { AfterCreateStep } from './requests-api.js';

/** Aviso del detalle cuando el envío desde el alta no tuvo respuesta. */
export const NOT_SUBMITTED_NOTICE = 'La solicitud se guardó como borrador, pero no se pudo enviar. Puedes enviarla desde aquí.';

/** Aviso del detalle cuando el documento elegido en el alta no tuvo respuesta. */
export const NOT_ATTACHED_NOTICE = 'La solicitud se guardó como borrador, pero no se pudo adjuntar el documento. Puedes adjuntarlo desde aquí.';

const NETWORK_NOTICES: Record<AfterCreateStep, string> = { document: NOT_ATTACHED_NOTICE, submit: NOT_SUBMITTED_NOTICE };

/**
 * Lo que el alta deja al detalle, por el **estado de la navegación** (nunca en la URL), cuando el borrador se
 * creó y falló un paso posterior: el paso (`document` o `submit`), la versión en que quedó el borrador y el
 * fallo. Es un objeto plano (el historial del navegador no guarda clases): el Problem Details tal como llegó,
 * sin respuesta (`network`) o una respuesta inesperada.
 */
const SubmitFailureSchema = z.object({
  submitFailure: z.object({
    step: z.enum(['document', 'submit']),
    version: z.number().int(),
    failure: z.discriminatedUnion('kind', [
      z.object({
        kind: z.literal('problem'),
        status: z.number().int(),
        title: z.string(),
        code: z.string().optional(),
        detail: z.string().optional(),
        traceId: z.string().optional(),
      }),
      z.object({ kind: z.literal('network') }),
      z.object({ kind: z.literal('unexpected') }),
    ]),
  }),
});

export type SubmitFailureState = z.infer<typeof SubmitFailureSchema>;

/**
 * Estado de navegación para el detalle del borrador recién creado, tras fallar el documento o el envío.
 * `version` es la del borrador cuando falló el paso: la del alta o, si el documento se adjuntó, la del documento.
 */
export function submitFailureState(step: AfterCreateStep, version: number, error: unknown): SubmitFailureState {
  if (error instanceof ApiProblem) {
    const { status, title, code, detail, traceId } = error;
    return { submitFailure: { step, version, failure: { kind: 'problem', status, title, code, detail, traceId } } };
  }
  return { submitFailure: { step, version, failure: { kind: isNetworkFailure(error) ? 'network' : 'unexpected' } } };
}

/**
 * En el detalle: el resultado del paso que falló tras el alta, una sola vez. Solo mientras la solicitud sigue
 * en esa versión y en `DRAFT`: cualquier cambio posterior (p. ej., adjuntar o enviar desde el detalle) lo retira.
 */
export function SubmitFailureFromForm({ state, request }: { state: unknown; request: PermissionRequest }) {
  const parsed = SubmitFailureSchema.safeParse(state);
  if (!parsed.success) return null;
  const { step, version, failure } = parsed.data.submitFailure;
  if (request.status !== 'DRAFT' || request.version !== version) return null;
  if (failure.kind === 'network') return <output className="block rounded-md border border-border bg-muted p-3 text-sm">{NETWORK_NOTICES[step]}</output>;
  const problem =
    failure.kind === 'problem' ? new ApiProblem(failure.status, failure.title, failure.code, failure.detail, failure.traceId, []) : undefined;
  return <ProblemMessage error={problem} />;
}
